import { supabase } from '../client/browser'

/**
 * Storage service for file operations
 */

type StorageService = {
  upload: (
    bucket: string,
    path: string,
    file: File,
    options?: {
      cacheControl?: string
      contentType?: string
      upsert?: boolean
    }
  ) => Promise<{ path: string }>
  getPublicUrl: (bucket: string, path: string) => string
  download: (bucket: string, path: string) => Promise<Blob>
  delete: (bucket: string, paths: string[]) => Promise<any[]>
  list: (
    bucket: string,
    folder: string,
    options?: {
      limit?: number
      offset?: number
      sortBy?: { column: string; order: 'asc' | 'desc' }
    }
  ) => Promise<any[]>
  move: (bucket: string, fromPath: string, toPath: string) => Promise<{ message: string }>
  copy: (bucket: string, fromPath: string, toPath: string) => Promise<{ path: string }>
  createSignedUrl: (bucket: string, path: string, expiresIn: number) => Promise<string>
}

export const storageService: StorageService = {
  /**
   * Upload a file to a storage bucket
   */
  async upload(
    bucket: string,
    path: string,
    file: File,
    options?: {
      cacheControl?: string
      contentType?: string
      upsert?: boolean
    }
  ) {
    console.log('[storageService] Uploading:', {
      bucket,
      path,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      options
    })

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: options?.cacheControl || '3600',
        upsert: options?.upsert || false,
        contentType: options?.contentType || file.type
      })

    if (error) {
      console.error('[storageService] Upload failed:', {
        bucket,
        path,
        error
      })
      throw error
    }

    console.log('[storageService] Upload successful:', data)
    return data
  },

  /**
   * Get public URL for a file
   */
  getPublicUrl(bucket: string, path: string): string {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)
    
    return data.publicUrl
  },

  /**
   * Download a file
   */
  async download(bucket: string, path: string) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(path)

    if (error) throw error
    return data
  },

  /**
   * Delete a file
   */
  async delete(bucket: string, paths: string[]) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .remove(paths)

    if (error) throw error
    return data
  },

  /**
   * List files in a folder
   */
  async list(bucket: string, folder: string, options?: {
    limit?: number
    offset?: number
    sortBy?: { column: string; order: 'asc' | 'desc' }
  }) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(folder, {
        limit: options?.limit || 100,
        offset: options?.offset || 0,
        sortBy: options?.sortBy
      })

    if (error) throw error
    return data
  },

  /**
   * Move/rename a file
   */
  async move(bucket: string, fromPath: string, toPath: string) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .move(fromPath, toPath)

    if (error) throw error
    return data
  },

  /**
   * Copy a file
   */
  async copy(bucket: string, fromPath: string, toPath: string) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .copy(fromPath, toPath)

    if (error) throw error
    return data
  },

  /**
   * Create a signed URL for temporary access
   */
  async createSignedUrl(bucket: string, path: string, expiresIn: number) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn)

    if (error) throw error
    return data.signedUrl
  }
}